/**
 * Housing Financing Page — /housing/financing
 * Decision-engine with: Financing Signal + Financial Sensitivity + Carbon Pathways + Readiness.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw, ArrowLeft, DollarSign, Shield, GitBranch,
  CheckCircle2, XCircle, ArrowRight, ChevronDown, ChevronUp,
  TrendingUp, TrendingDown, AlertTriangle, Info
} from "lucide-react";
import { useHousingStatus } from "@/hooks/useHousingStatus";
import { useHousingJourney } from "@/hooks/useHousingJourney";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
// ── Financial Sensitivity Table ────────────────────────────────────────────────
function SensitivityTable({ sensitivity }) {
  if (!sensitivity) return null;
  const base     = sensitivity.base    || {};
  const scenarios= sensitivity.scenarios || [];
  const payback  = sensitivity.payback_analysis || {};

  const CAPEX_S  = scenarios.filter(s => s.variable === "capex");
  const ENERGY_S = scenarios.filter(s => s.variable === "energy");
  const CARBON_S = scenarios.filter(s => s.variable === "carbon_price");

  function DeltaBadge({ pct }) {
    if (!pct && pct !== 0) return null;
    const pos = pct > 0;
    return (
      <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${pos ? "text-red-600" : "text-emerald-600"}`}>
        {pos ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
        {pos ? "+" : ""}{pct}%
      </span>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="sensitivity-table">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
        <p className="text-sm font-bold text-slate-900">Financial Sensitivity Analysis</p>
        <p className="text-xs text-slate-500 mt-0.5">
          ±10% CAPEX &amp; Energy demand · ±20% Carbon price · All other variables held constant
        </p>
      </div>

      {/* CAPEX Sensitivity */}
      <div className="px-5 py-4 border-b border-slate-100">
        <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-3">
          CAPEX Sensitivity — Impact on Cost per tCO₂e
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100">
                {["Scenario","CAPEX/unit","Cost/tCO₂e","vs Base"].map(h => (
                  <th key={h} className="text-left text-xs text-slate-400 pb-1.5 pr-3 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-50">
                <td className="py-1.5 pr-3 text-slate-600 font-semibold">Base</td>
                <td className="py-1.5 pr-3 text-slate-700">${(base.capex_usd||0).toLocaleString()}</td>
                <td className="py-1.5 pr-3 font-bold text-teal-700">${base.cost_per_tco2e}</td>
                <td className="py-1.5 text-slate-400 text-xs">—</td>
              </tr>
              {CAPEX_S.map(s => (
                <tr key={s.id} className="border-b border-slate-50">
                  <td className="py-1.5 pr-3 text-slate-600">{s.label}</td>
                  <td className="py-1.5 pr-3 text-slate-700">${(s.capex_usd||0).toLocaleString()}</td>
                  <td className="py-1.5 pr-3 font-bold text-slate-800">${s.cost_per_tco2e}</td>
                  <td className="py-1.5"><DeltaBadge pct={s.delta_cost_pct} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Energy Sensitivity */}
      <div className="px-5 py-4 border-b border-slate-100">
        <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-3">
          Energy Demand Sensitivity — Impact on Annual Savings
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100">
                {["Scenario","Op. Reduction","Energy Savings/yr","vs Base"].map(h => (
                  <th key={h} className="text-left text-xs text-slate-400 pb-1.5 pr-3 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-50">
                <td className="py-1.5 pr-3 text-slate-600 font-semibold">Base</td>
                <td className="py-1.5 pr-3 text-slate-700">{base.op_reduction_tco2e_yr} tCO₂e/yr</td>
                <td className="py-1.5 pr-3 font-bold text-emerald-700">${base.energy_savings_usd_yr}/unit/yr</td>
                <td className="py-1.5 text-slate-400 text-xs">—</td>
              </tr>
              {ENERGY_S.map(s => (
                <tr key={s.id} className="border-b border-slate-50">
                  <td className="py-1.5 pr-3 text-slate-600">{s.label}</td>
                  <td className="py-1.5 pr-3 text-slate-700">{s.op_reduction_tco2e_yr} tCO₂e/yr</td>
                  <td className="py-1.5 pr-3 font-bold text-slate-800">${s.energy_savings_usd_yr}/unit/yr</td>
                  <td className="py-1.5"><DeltaBadge pct={s.delta_savings_pct} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Carbon Price Sensitivity */}
      <div className="px-5 py-4 border-b border-slate-100">
        <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-3">
          Carbon Price Sensitivity — Programme Revenue Range (3yr, 1,000 units)
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100">
                {["Scenario","Price Range","3yr Revenue Low","3yr Revenue High"].map(h => (
                  <th key={h} className="text-left text-xs text-slate-400 pb-1.5 pr-3 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-50">
                <td className="py-1.5 pr-3 text-slate-600 font-semibold">Base ($15–$30/t)</td>
                <td className="py-1.5 pr-3 text-slate-700">$15–$30/t</td>
                <td className="py-1.5 pr-3 font-bold text-blue-700">${(base.carbon_rev_low_3yr_prog||0).toLocaleString()}</td>
                <td className="py-1.5 pr-3 font-bold text-blue-700">${(base.carbon_rev_high_3yr_prog||0).toLocaleString()}</td>
              </tr>
              {CARBON_S.map(s => (
                <tr key={s.id} className="border-b border-slate-50">
                  <td className="py-1.5 pr-3 text-slate-600">{s.label}</td>
                  <td className="py-1.5 pr-3 text-slate-700">{s.price_range_usd_t}</td>
                  <td className="py-1.5 pr-3 font-bold text-slate-800">${(s.carbon_rev_low_3yr_prog||0).toLocaleString()}</td>
                  <td className="py-1.5 pr-3 font-bold text-slate-800">${(s.carbon_rev_high_3yr_prog||0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-amber-600 mt-2 italic flex items-start gap-1">
          <AlertTriangle size={9} className="flex-shrink-0 mt-0.5" />
          Carbon revenue is speculative — must NOT be included in debt service calculations until independently certified.
        </p>
      </div>

      {/* Payback */}
      <div className="px-5 py-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Simple Payback (Energy Savings Only)</p>
            <p className="text-xs text-slate-400 italic mt-0.5">{payback.note}</p>
          </div>
          <div className="flex-shrink-0 text-right">
            <p className="text-lg font-bold text-slate-900">{payback.simple_payback_years_per_unit} <span className="text-xs font-normal text-slate-400">yr/unit</span></p>
            <p className="text-xs text-slate-500">${(payback.annual_savings_usd_per_unit||0).toFixed(0)}/unit/yr guaranteed savings</p>
          </div>
        </div>
      </div>

      <div className="px-5 pb-3">
        <p className="text-xs text-slate-400 italic">{sensitivity.methodology_note}</p>
      </div>
    </div>
  );
}

// ── Carbon Pathway Readiness Block ─────────────────────────────────────────────
function CarbonPathwayBlock({ carbonPathway }) {
  const [expanded, setExpanded] = useState("insetting");
  if (!carbonPathway) return null;

  const pathways = carbonPathway.pathways || {};
  const ORDER    = ["insetting","vcm","poa","article6"];
  const LABELS   = { insetting: "Insetting", vcm: "VCM", poa: "PoA", article6: "Article 6" };

  const BADGE_STYLES = {
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-100 text-amber-700 border-amber-300",
    slate:   "bg-slate-100 text-slate-600 border-slate-200",
    red:     "bg-red-50 text-red-600 border-red-200",
  };
  const STATUS_ICON = {
    recommended: <CheckCircle2 size={13} className="text-emerald-500" />,
    conditional: <AlertTriangle size={13} className="text-amber-500" />,
    below_scale: <XCircle size={13} className="text-slate-400" />,
    viable:      <CheckCircle2 size={13} className="text-amber-500" />,
    not_eligible:<XCircle size={13} className="text-red-400" />,
    early_stage: <AlertTriangle size={13} className="text-slate-400" />,
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="carbon-pathway-block">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
        <p className="text-sm font-bold text-slate-900">Carbon Pathway Readiness</p>
        <p className="text-xs text-slate-500 mt-0.5">Live assessment · Not a carbon eligibility determination</p>
      </div>

      {ORDER.map(key => {
        const p = pathways[key];
        if (!p) return null;
        const isOpen = expanded === key;
        const reqs   = p.requirements || [];
        const metCount = reqs.filter(r => r.met).length;

        return (
          <div key={key} className="border-b border-slate-100 last:border-0" data-testid={`pathway-row-${key}`}>
            <button
              className="w-full px-5 py-3 flex items-center justify-between gap-3 text-left hover:bg-slate-50"
              onClick={() => setExpanded(isOpen ? null : key)}
            >
              <div className="flex items-center gap-2.5 flex-1 min-w-0">
                {STATUS_ICON[p.status] || <Info size={13} className="text-slate-400" />}
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-900">{LABELS[key]}</p>
                  <p className="text-xs text-slate-400 truncate">{p.label}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {reqs.length > 0 && (
                  <span className="text-xs text-slate-400">{metCount}/{reqs.length} req.</span>
                )}
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${BADGE_STYLES[p.badge_color] || BADGE_STYLES.slate}`}>
                  {p.badge}
                </span>
                {isOpen ? <ChevronUp size={12} className="text-slate-400" /> : <ChevronDown size={12} className="text-slate-400" />}
              </div>
            </button>

            {isOpen && (
              <div className="px-5 pb-4 pt-1 bg-slate-50 border-t border-slate-100">
                <p className="text-xs text-slate-600 leading-snug mb-3">{p.reasoning}</p>

                {reqs.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1.5">Requirements</p>
                    <div className="space-y-1">
                      {reqs.map((r, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-xs">
                          {r.met
                            ? <CheckCircle2 size={11} className="text-emerald-500 flex-shrink-0" />
                            : <XCircle     size={11} className="text-slate-300 flex-shrink-0" />}
                          <span className={r.met ? "text-slate-700" : "text-slate-400"}>{r.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-1.5 bg-blue-50 rounded-lg px-3 py-2">
                  <ArrowRight size={10} className="text-blue-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700 leading-snug">{p.next_action}</p>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="px-5 py-2.5 bg-slate-50 border-t border-slate-100">
        <p className="text-xs text-slate-400 italic">{carbonPathway.methodology_note}</p>
      </div>
    </div>
  );
}

// ── Admin Completion Note ────────────────────────────────────────────────────
function AdminCompletionNote({ note }) {
  if (!note) return null;
  return (
    <div className="border border-amber-200 bg-amber-50 rounded-xl px-4 py-3 flex items-start gap-2.5" data-testid="admin-completion-note">
      <Info size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-0.5">Admin Completion Workflow</p>
        <p className="text-xs text-amber-600 leading-snug">{note.slice(0, 200)}</p>
      </div>
    </div>
  );
}

// ── OPEX / Revenue Model ────────────────────────────────────────────────────
function FinancialModelSection({ financialModel }) {
  if (!financialModel) return null;
  const opex = financialModel.opex_estimate   || {};
  const rev  = financialModel.revenue_model   || {};
  const sp   = financialModel.simple_payback  || {};
  const asn  = financialModel.assumptions     || {};

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="financial-model-section">
      <div className="px-5 py-3 bg-teal-50 border-b border-teal-100">
        <p className="text-sm font-bold text-teal-800">OPEX &amp; Revenue Model</p>
        <p className="text-xs text-teal-600">Operating cost baseline · Energy savings (guaranteed) · Carbon revenue (speculative)</p>
      </div>
      <div className="divide-y divide-slate-50">
        {/* OPEX */}
        <div className="px-5 py-3">
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-2">Operating Costs (Energy) — Per Unit/Year</p>
          {[
            ["Baseline energy cost",    `$${opex.baseline_energy_cost_usd_yr}/yr`,   "text-slate-700"],
            ["Project energy cost",     `$${opex.project_energy_cost_usd_yr}/yr`,    "text-slate-700"],
            ["OPEX saving (solar+insul)",`$${opex.opex_saving_per_unit_usd_yr}/yr`,   "text-emerald-700 font-bold"],
            ["Programme OPEX saving",   `$${(opex.opex_saving_programme_usd_yr||0).toLocaleString()}/yr`, "text-emerald-700 font-bold"],
          ].map(([l, v, c]) => (
            <div key={l} className="flex justify-between text-xs py-0.5">
              <span className="text-slate-500">{l}</span>
              <span className={c}>{v}</span>
            </div>
          ))}
          <p className="text-xs text-slate-400 italic mt-1">{opex.note}</p>
        </div>

        {/* Revenue model */}
        <div className="px-5 py-3">
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-2">Revenue Model</p>
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Energy savings (guaranteed)</span>
              <span className="font-bold text-emerald-700">${rev.energy_savings_usd_yr_per_unit}/unit/yr · ${(rev.energy_savings_usd_yr_programme||0).toLocaleString()}/yr programme</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Carbon revenue — low (speculative)</span>
              <span className="font-bold text-blue-600">${(rev.carbon_rev_low_3yr_programme||0).toLocaleString()} (3yr)</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Carbon revenue — high (speculative)</span>
              <span className="font-bold text-blue-600">${(rev.carbon_rev_high_3yr_programme||0).toLocaleString()} (3yr)</span>
            </div>
          </div>
          <p className="text-xs text-amber-600 italic mt-1.5">{rev.carbon_rev_disclaimer}</p>
        </div>

        {/* Assumptions */}
        <div className="px-5 py-3">
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5">Assumptions</p>
          <div className="flex flex-wrap gap-2">
            {[
              `Energy tariff: $${asn.energy_tariff_usd_kwh}/kWh`,
              `Grid factor: ${asn.grid_factor_kgco2e_kwh} kgCO₂e/kWh`,
              `Carbon price: ${asn.carbon_price_range_usd_t}/t`,
              `Project life: ${asn.project_life_years} yr`,
            ].map(a => (
              <span key={a} className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{a}</span>
            ))}
          </div>
          <p className="text-xs text-slate-400 italic mt-1">{asn.source}</p>
        </div>
      </div>
    </div>
  );
}

// ── MAIN PAGE ──────────────────────────────────────────────────────────────────
export default function HousingFinancing() {
  const navigate = useNavigate();
  const {
    review_status, stage, readiness, badgeClass, ringClass, readinessBadge, readinessLabel,
    isBankable, isActionRequired, isVerified, nextStep
  } = useHousingStatus();
  const { loading, journey, org, demo, prog, kpis,
          sensitivity, financialModel, carbonPathway } = useHousingJourney();
  const adminNote = journey?.admin_completion_note;
  const fr        = journey?.financing_readiness || {};

  if (loading) return <div className="flex items-center justify-center min-h-[40vh]"><RefreshCw size={18} className="animate-spin text-teal-500" /></div>;

  const signalLabel = fr.signal || "MODERATE";
  const signalContext = signalLabel === "STRONG"
    ? "Suitable for priority green finance engagement"
    : signalLabel === "MODERATE"
    ? "Suitable for green lending (subject to lender due diligence)"
    : "Additional work required before finance engagement";

  const recommendation = isBankable
    ? {
        title: "Eligible for Financing",
        items: [
          { ok: true,  text: "Suitable for green construction finance" },
          { ok: true,  text: "Eligible for carbon monetisation (subject to verification)" },
          { ok: true,  text: "Verified climate credentials available" },
        ],
        cta: "Engage lenders and carbon buyers",
        ctaPath: "/housing/reports",
        ctaColor: "bg-emerald-600 text-white hover:bg-emerald-700",
      }
    : {
        title: "Conditions for Full Financing",
        items: [
          { ok: true,  text: "Suitable for green lending (with conditions)" },
          { ok: false, text: "Not yet ready for carbon monetisation" },
          { ok: false, text: "Verification not yet completed" },
        ],
        cta: nextStep,
        ctaPath: isActionRequired ? "/housing/evidence-vault" : "/housing/carbon-structuring",
        ctaColor: "bg-amber-600 text-white hover:bg-amber-700",
      };

  return (
    <div className="p-6 lg:p-8 min-h-screen">
        <button onClick={() => navigate("/housing-project")} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
          <ArrowLeft size={13} /> Housing Project
        </button>

          
      <PageHeader
        title="Financing"
        subtitle="Investment case for the low-carbon housing programme"
        backUrl="/housing-project"
        backLabel="Housing Project"
      />

        {/* Admin completion note — honest and visible */}
        {adminNote && <div className="mb-5"><AdminCompletionNote note={adminNote} /></div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Left 2/3 */}
          <div className="lg:col-span-2 space-y-5">

            {/* Financing Signal */}
            <div className={`rounded-xl p-6 border-2 ${signalLabel === "STRONG" ? "border-emerald-300 bg-emerald-50" : signalLabel === "MODERATE" ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}
              data-testid="financing-signal">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Financing Signal</p>
                  <p className="text-2xl font-bold text-slate-900">{signalLabel}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{signalContext}</p>
                </div>
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${signalLabel === "STRONG" ? "bg-emerald-100" : "bg-amber-100"}`}>
                  <Shield size={18} className={signalLabel === "STRONG" ? "text-emerald-600" : "text-amber-600"} />
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100/80 flex items-center justify-between">
                <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Readiness Status</p>
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${readinessBadge}`} data-testid="readiness-badge">
                  {readinessLabel}
                </span>
              </div>
            </div>

            {/* Financing Recommendation */}
            <div className={`rounded-xl p-5 border-2 ${isBankable ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}
              data-testid="financing-recommendation">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Financing Recommendation</p>
              <p className="text-sm font-bold text-slate-900 mb-3">{recommendation.title}</p>
              <div className="space-y-2 mb-4">
                {recommendation.items.map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-xs text-slate-700">
                    {item.ok
                      ? <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />
                      : <XCircle     size={14} className="text-slate-400 flex-shrink-0"  />}
                    {item.text}
                  </div>
                ))}
              </div>
              <button onClick={() => navigate(recommendation.ctaPath)}
                className={`w-full flex items-center justify-center gap-2 py-2.5 text-xs font-bold rounded-xl transition-colors ${recommendation.ctaColor}`}
                data-testid="recommendation-cta">
                → {recommendation.cta} <ArrowRight size={12} />
              </button>
            </div>

            {/* Project Status Strip */}
            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="structured-status">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Project Status</p>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: "Review Status", value: review_status, color: badgeClass },
                  { label: "Stage",         value: stage,         color: "bg-slate-100 text-slate-600" },
                  { label: "Readiness",     value: readiness,     color: readinessBadge },
                ].map(({ label, value, color }) => (
                  <div key={label} className="text-center">
                    <p className="text-xs text-slate-400 mb-1">{label}</p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${color}`}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* OPEX + Revenue Model */}
            {financialModel && <FinancialModelSection financialModel={financialModel} />}

            {/* Financial Sensitivity */}
            {sensitivity && <SensitivityTable sensitivity={sensitivity} />}

            {/* Carbon-Linked Financing */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-blue-800">Carbon-Linked Financing</p>
                  <p className="text-xs text-blue-600">Applicable when carbon pathway eligibility is achieved</p>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${isBankable ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-300"}`}>
                  {isBankable ? "Eligible" : "Conditional"}
                </span>
              </div>
              <div className="px-5 py-4">
                <p className="text-xs text-slate-500 italic mb-3">
                  Carbon revenue-backed financing — subject to certification and market conditions.
                </p>
                {[
                  ["VCM value (indicative)", `$${Math.round((prog.cumulative_3yr_tco2e||0)*15).toLocaleString()}–$${Math.round((prog.cumulative_3yr_tco2e||0)*30).toLocaleString()}`, "3yr · $15–$30/t"],
                  ["Primary pathway", "Insetting (immediate)", "No credit issuance required"],
                  ["VCM pathway", "Conditional — scale met", "Requires methodology validation"],
                  ["Article 6", "Not eligible (current stage)", "Requires DNA authorization"],
                ].map(([l, v, sub]) => (
                  <div key={l} className="flex items-start justify-between py-1.5 border-b border-slate-50 last:border-0">
                    <div><p className="text-xs font-semibold text-slate-700">{l}</p><p className="text-xs text-slate-400">{sub}</p></div>
                    <span className="text-xs font-bold text-blue-700 text-right ml-3 flex-shrink-0">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Carbon Pathway Readiness Block */}
            {carbonPathway && <CarbonPathwayBlock carbonPathway={carbonPathway} />}

          </div>

          {/* Right 1/3 */}
          <div className="space-y-4">
            <div className="bg-[#0F172A] rounded-xl p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Key Numbers</p>
              <div className="space-y-3">
                {[
                  ["Programme Impact", `~${kpis.programme_impact_tco2e?.toLocaleString()} tCO₂e`, "3yr indicative", "text-teal-400"],
                  ["Per Unit",         `~${kpis.per_unit_reduction_tco2e} tCO₂e`,                 "3yr cumulative",  "text-white"],
                  ["Readiness",        readiness,                                                   "",               isBankable ? "text-emerald-400" : "text-amber-400"],
                ].map(([l, v, sub, c]) => (
                  <div key={l} className="bg-white/5 rounded-xl px-3 py-2.5">
                    <p className="text-xs text-slate-500">{l}</p>
                    <p className={`text-sm font-bold ${c}`}>{v}</p>
                    {sub && <p className="text-xs text-slate-600 italic">{sub}</p>}
                  </div>
                ))}
              </div>
            </div>

            {/* Project Financing sidebar */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 bg-teal-50 border-b border-teal-200">
                <p className="text-sm font-bold text-teal-800">Project Financing</p>
                <p className="text-xs text-teal-600">Green construction finance · ESG capital · Blended finance</p>
              </div>
              <div className="px-5 py-4 space-y-2.5">
                {[
                  ["Programme Capex",  `$${((prog.capex_total_usd||0)/1000000).toFixed(1)}M`, "Incremental over conventional", "text-slate-900"],
                  ["Energy Savings",   `$${prog.energy_savings_total_usd_yr?.toLocaleString()}/yr`, "Annual cost reduction", "text-emerald-700"],
                  ["Cost/tCO₂e",       `$${demo.cost_per_tco2e}`,   "Lifetime · within IFC EDGE",   "text-teal-700"],
                ].map(([l, v, sub, c]) => (
                  <div key={l} className="flex items-start justify-between">
                    <div><p className="text-xs font-semibold text-slate-700">{l}</p><p className="text-xs text-slate-400">{sub}</p></div>
                    <span className={`text-sm font-bold ${c}`}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            <SectionCard>

              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Carbon Pathways Summary</p>
              {carbonPathway && Object.entries(carbonPathway.pathways || {}).map(([k, p]) => {
                const COLOR = {
                  recommended:"text-emerald-700 bg-emerald-50 border-emerald-200",
                  conditional:"text-amber-700 bg-amber-50 border-amber-200",
                  viable:     "text-amber-700 bg-amber-50 border-amber-200",
                  below_scale:"text-slate-500 bg-slate-50 border-slate-200",
                  not_eligible:"text-red-600 bg-red-50 border-red-200",
                  early_stage:"text-slate-500 bg-slate-50 border-slate-200",
                }[p.status] || "text-slate-500 bg-slate-50 border-slate-200";
                const NAMES = { insetting:"Insetting", vcm:"VCM", poa:"PoA", article6:"Article 6" };
                return (
                  <div key={k} className="flex items-center justify-between py-1 text-xs">
                    <span className="text-slate-600 font-semibold">{NAMES[k] || k}</span>
                    <span className={`px-2 py-0.5 rounded-full border text-xs font-bold ${COLOR}`}>
                      {p.status.replace(/_/g," ")}
                    </span>
                  </div>
                );
              })}
              <button onClick={() => navigate("/housing/carbon-strategy")}
                className="w-full mt-3 flex items-center gap-2 px-4 py-2 border border-teal-200 text-teal-700 text-xs font-bold rounded-xl hover:bg-teal-50">
                <GitBranch size={13} /> Full Carbon Strategy
              </button>
                      </SectionCard>

            <SectionCard>

              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Finance Brief</p>
              <p className="text-xs text-slate-500 mb-3">Download the bank-facing Finance Brief.</p>
              <button onClick={() => navigate("/housing/reports")}
                className="w-full flex items-center gap-2 px-4 py-2.5 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800">
                <DollarSign size={13} /> View Reports
              </button>
                      </SectionCard>
          </div>
        </div>
    </div>
  );
}
